import { Dispatch, SetStateAction } from "react"
import { NavigateFunction } from "react-router-dom"
import { Node } from "reactflow"
import { VehicleModulesResearchResponse } from "../../../models/response/VehicleModulesResearchResponse"
import { ModuleView, VehicleModule } from '../../../models/VehicleModule'
import { VehicleModulesService } from '../../../services/VehicleModulesService'
import { VehicleService } from '../../../services/VehicleService'
import { ModuleTreeConfig } from '../ModulesResearch'

const MODULE_NODE_HEIGHT = 75
const PICTURE_COLLISION_HEIGHT = 378
const CAR_NODE_HEIGHT = 65

export const buildModuleNodes = (
	data: VehicleModulesResearchResponse | null,
	setTriggerUpdate: Dispatch<SetStateAction<number>>,
	navigate: NavigateFunction,
	onModuleHover: (event: React.MouseEvent, data: ModuleView) => void,
	onModuleLeave: () => void
) => {
	if (!data) return []
	const modulesNodes = getModulesNodes(data, setTriggerUpdate, navigate, onModuleHover, onModuleLeave)
	const modulesVerticalCenter = getModulesVerticalCenter(modulesNodes)
	const pictureY = modulesVerticalCenter - PICTURE_COLLISION_HEIGHT / 2
	const pictureCenterY = pictureY + PICTURE_COLLISION_HEIGHT / 2
	const nodes: Node<any, string | undefined>[] = [
		getLeftVehicleNode(data, pictureCenterY),
		getVehiclePictureNode(data, pictureY),
		getContentNode(data, navigate, setTriggerUpdate, pictureY),
		...modulesNodes,
	]
	if (!data.childVehicle) return nodes
	nodes.push(getNextVehicleNode(data, navigate))
	return nodes
}

const getModulesVerticalCenter = (modulesNodes: Node<any, string | undefined>[]) => {
	if (modulesNodes.length === 0) return ModuleTreeConfig.VERTICAL_PADDING + MODULE_NODE_HEIGHT / 2
	const top = Math.min(...modulesNodes.map((node) => node.position.y))
	const bottom = Math.max(...modulesNodes.map((node) => node.position.y)) + MODULE_NODE_HEIGHT
	return (top + bottom) / 2
}

const getContentNode = (data: VehicleModulesResearchResponse, navigate: NavigateFunction, setTriggerUpdate: Dispatch<SetStateAction<number>>, pictureY: number) => {

	const x = ModuleTreeConfig.MAIN_LEFT_PADDING +
		ModuleTreeConfig.CAR_NODE_WIDTH +
		ModuleTreeConfig.GAP +
		ModuleTreeConfig.PICTURE_WIDTH / 2 -
		ModuleTreeConfig.CONTENT_ZONE_WIDTH / 1.5
	const y = pictureY + ModuleTreeConfig.PICTURE_HEIGHT * 0.65

	const onClick = async () => {
		if (data.vehicle.bought) {
			const vehicleId = data.vehicle.vehicle.id
			localStorage.setItem('lastVehicleId', String(vehicleId))
			navigate('/main')
			return
		} else if (data.vehicle.researched) {
			return await VehicleService.buyVehicle(data.vehicle.vehicle.id)
		} else {
			return await VehicleService.researchVehicle(data.vehicle.vehicle.id)
		}
	}

	const onPreview = async () => {
		navigate(`/preview?vehicleId=${data.vehicle.vehicle.id}`)
	}

	return {
		id: "content-node",
		data: {
			isBought: data.vehicle.bought,
			isResearched: data.vehicle.researched,
			isParentBoughtOrResearched: data.parentVehicle?.bought || data.parentVehicle?.researched,
			experience: data.vehicle.experience,
			setTriggerUpdate: setTriggerUpdate,
			vehicleId: data.vehicle.vehicle.id,
			onClick: onClick,
			price: data.vehicle.vehicle.price,
			xpPrice: data.vehicle.vehicle.xpCost,
			onPreview: onPreview
		},
		position: { x: x, y: y },
		type: 'content'
	}
}

const getNextVehicleNode = (data: VehicleModulesResearchResponse, navigate: NavigateFunction) => {
	const vehicle = data.childVehicle.vehicle
	return {
		id: "right-vehicle",
		data: {
			name: vehicle.name,
			brand: vehicle.brand,
			img: vehicle.modelName,
			checked: false,
			button: undefined,
			isInModules: true,
			onClick: () => {
				navigate(`/modules-research?id=${vehicle.id}`)
			},
			setTriggerUpdate: () => { },
		},
		position: { x: 1750, y: ModuleTreeConfig.VERTICAL_PADDING + 5 },
		type: 'car'
	}
}

const getModulesNodes = (
	data: VehicleModulesResearchResponse,
	setTriggerUpdate: Dispatch<SetStateAction<number>>,
	navigate: NavigateFunction,
	onModuleHover: (event: React.MouseEvent, data: ModuleView) => void,
	onModuleLeave: () => void
) => {
	const nodes: Node<any, string | undefined>[] = []

	const firstElement = {
		x:
			ModuleTreeConfig.MAIN_LEFT_PADDING +
			ModuleTreeConfig.CAR_NODE_WIDTH +
			ModuleTreeConfig.GAP +
			ModuleTreeConfig.PICTURE_WIDTH,
		y: ModuleTreeConfig.VERTICAL_PADDING
	}


	const map = (index: number, module: VehicleModule, nodes: Node<any, string | undefined>[]) => {
		const moduleType = module.moduleName
		const xIndex = nodes.filter((node) => node.data.type === moduleType).length
		let yIndex = 0
		switch (moduleType) {
			case 'ENGINE':
				yIndex = 0
				break
			case 'BRAKES':
				yIndex = 1
				break
			case 'TRANSMISSION':
				yIndex = 2
				break
			case 'TURBO':
				yIndex = 3
				break
			case 'TYRES':
				yIndex = 4
				break
		}

		const x = firstElement.x + ModuleTreeConfig.MODULES_HORIZONTAL_GAP * 2 * xIndex
		const y = firstElement.y + ModuleTreeConfig.MODULES_VERTICAL_GAP * 2 * yIndex

		const id = xIndex === 0 ? `module-${moduleType}-0` : `module-${moduleType}-${xIndex}`

		const getBuyButtonIfNeeded = () => {
			if (module.bought) return undefined
			if (module.researched) {
				return {
					type: 'BUY',
					price: module.price,
					onClick: () => VehicleModulesService.buyOrResearch(data.vehicle.vehicle.id, module.id)
				}
			}
			const parentNodeId = xIndex === 0 ? `module-${moduleType}-0` : `module-${moduleType}-${xIndex - 1}`
			const parentNode = nodes.find((node) => node.id === parentNodeId)
			if (parentNode?.data.isResearched || parentNode?.data.bought) return {
				type: 'RESEARCH',
				price: module.price,
				onClick: () => VehicleModulesService.buyOrResearch(data.vehicle.vehicle.id, module.id)
			}
		}

		return {
			id: id,
			data: {
				name: module.name,
				moduleKey: module.moduleKey,
				moduleId: module.id,
				vehicleId: data.vehicle.vehicle.id,
				icon: module.icon,
				type: moduleType,
				button: getBuyButtonIfNeeded(),
				isShown: true,
				isDarken: !getBuyButtonIfNeeded(),
				isResearched: module.researched,
				isBought: module.bought,
				isEquipped: module.equipped,
				level: module.level,
				setTriggerUpdate: setTriggerUpdate,
				onHover: onModuleHover,
				onLeave: onModuleLeave,
				otherData: {
					price: module.otherInfo?.buyPrice,
					xpPrice: module.otherInfo?.researchPrice,
					technicalCharacteristics: module.otherInfo.technicalCharacteristics,
					effects: module.otherInfo.effects
				}
			},
			position: { x: x, y: y },
			type: 'module'
		}
	}

	for (let i = 0; i < data.modules.length; i++) {
		const node = map(i, data.modules[i], nodes)
		nodes.push(node)
	}

	return nodes
}

const getVehiclePictureNode = (data: VehicleModulesResearchResponse, pictureY: number) => {
	const vehicle = data.vehicle.vehicle

	const leftPadding = ModuleTreeConfig.MAIN_LEFT_PADDING
	const x = leftPadding + ModuleTreeConfig.CAR_NODE_WIDTH + ModuleTreeConfig.GAP
	return {
		id: "vehicle-picture",
		data: {
			name: vehicle.name,
		},
		position: { x: x, y: pictureY },
		type: 'picture'
	}
}

const getLeftVehicleNode = (data: VehicleModulesResearchResponse, pictureCenterY: number) => {
	const vehicle = data.vehicle.vehicle
	return {
		id: "left-vehicle",
		data: {
			name: vehicle.name,
			brand: vehicle.brand,
			img: vehicle.modelName,
			checked: false,
			button: undefined,
			isInModules: true,
			setTriggerUpdate: () => { },
		},
		position: { x: ModuleTreeConfig.MAIN_LEFT_PADDING, y: pictureCenterY - CAR_NODE_HEIGHT / 2 },
		type: 'car'
	}
}
